import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, XCircle, AlertOctagon, Loader2, ArrowLeft, Camera, ScanLine } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, adminApi, ASSETS } from "@/lib/api";
import { KINDS, fmtTime } from "@/pages/TicketVerify";
import { DoorSummary } from "@/components/DoorSummary";

const PASS_RE = /\/(tickets|rsvp)\/verify\/([A-Za-z0-9-]+)/;
const AUTO_RESUME_MS = 4000;

function beep(ok) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = ok ? "sine" : "square";
    osc.frequency.value = ok ? 1046 : 220;
    gain.gain.value = 0.25;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + (ok ? 0.18 : 0.5));
    navigator.vibrate?.(ok ? 120 : [200, 100, 200]);
  } catch {
    /* sound not available */
  }
}

async function processScan(text) {
  const m = text.match(PASS_RE);
  if (!m) return { tone: "bad", title: "Not A Trio Pass", detail: "This QR isn't a Trio ticket or RSVP pass." };
  const k = KINDS[m[1] === "tickets" ? "ticket" : "rsvp"];
  const id = m[2];
  let t;
  try {
    t = k.normalize((await api.get(k.get(id))).data);
  } catch {
    return { tone: "bad", title: `${k.noun} Not Found` };
  }
  const info = { name: t.name, count: k.countLabel(t.count), what: t.what, noun: k.noun };
  if (!t.valid) return { tone: "bad", title: "Not Valid", detail: `Status: ${t.status}`, ...info };
  if (t.checked_in_at) {
    return { tone: "used", title: "Already Checked In", detail: `Used ${fmtTime(t.checked_in_at)}${t.checked_in_by ? ` by ${t.checked_in_by}` : ""}`, ...info };
  }
  try {
    await adminApi.post(k.checkIn(id));
    return { tone: "ok", title: "Checked In", ...info };
  } catch (e) {
    if (e.response?.status === 409) return processScan(text);
    return { tone: "bad", title: "Couldn't Check In", detail: e.response?.data?.detail || "Try again", ...info };
  }
}

const TONES = {
  ok: { bg: "bg-green-600", Icon: CheckCircle2 },
  used: { bg: "bg-red-600", Icon: AlertOctagon },
  bad: { bg: "bg-red-700", Icon: XCircle },
};

const ResultOverlay = ({ result, onNext }) => {
  const { bg, Icon } = TONES[result.tone];
  return (
    <button type="button" onClick={onNext} data-testid={`door-result-${result.tone}`} className={`fixed inset-0 z-50 ${bg} text-white flex flex-col items-center justify-center px-6 text-center`}>
      <Icon className="w-28 h-28 mb-4" />
      <div data-testid="door-result-title" className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-none">{result.title}</div>
      {result.count && <div data-testid="door-result-count" className="font-display uppercase text-3xl mt-4">{result.count}</div>}
      {result.name && <div data-testid="door-result-name" className="text-2xl font-semibold mt-2">{result.name}</div>}
      {result.what && <div className="text-sm opacity-80 mt-1">{result.noun} · {result.what}</div>}
      {result.detail && <div data-testid="door-result-detail" className="text-base mt-3 opacity-90">{result.detail}</div>}
      <div className="mt-10 border-2 border-white px-8 py-4 uppercase tracking-widest text-sm font-bold">Tap To Scan Next</div>
    </button>
  );
};

export default function DoorScanner() {
  const { user, loading } = useAuth();
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const startingRef = useRef(false);
  const timerRef = useRef(null);
  const [result, setResult] = useState(null);
  const [working, setWorking] = useState(false);
  const [camError, setCamError] = useState("");
  const [count, setCount] = useState(0);

  const resume = useCallback(() => {
    clearTimeout(timerRef.current);
    setResult(null);
    busyRef.current = false;
    try { scannerRef.current?.resume(); } catch { /* not paused */ }
  }, []);

  const onScan = useCallback(async (text) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try { scannerRef.current?.pause(true); } catch { /* ignore */ }
    setWorking(true);
    const r = await processScan(text);
    setWorking(false);
    beep(r.tone === "ok");
    if (r.tone === "ok") setCount((c) => c + 1);
    setResult(r);
    timerRef.current = setTimeout(resume, AUTO_RESUME_MS);
  }, [resume]);

  const start = useCallback(async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setCamError("");
    try {
      const s = scannerRef.current || new Html5Qrcode("door-reader", { verbose: false });
      scannerRef.current = s;
      if (s.isScanning) return;
      await s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } }, onScan, () => {});
    } catch (e) {
      if (scannerRef.current?.isScanning) return;
      setCamError("Camera blocked. Allow camera access for this site in your phone settings, then tap Start Camera.");
    } finally {
      startingRef.current = false;
    }
  }, [onScan]);

  useEffect(() => {
    if (!user) return undefined;
    start();
    return () => {
      clearTimeout(timerRef.current);
      const s = scannerRef.current;
      if (s?.isScanning) s.stop().catch(() => {});
    };
  }, [user, start]);

  if (loading) return <div className="min-h-screen bg-black" />;
  if (!user) return <Navigate to="/staff/login?next=/door" replace />;

  return (
    <div data-testid="door-scanner" className="min-h-screen bg-black text-white flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <header className="flex items-center justify-between px-4 h-14 border-b border-white/10">
        <Link to={user.role === "staff" ? "/staff" : "/admin"} data-testid="door-back" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <img src={ASSETS.logo} alt="Trio" className="h-7 w-auto invert" />
        <div data-testid="door-count" className="text-xs uppercase tracking-widest text-[var(--trio-accent)]">{count} in</div>
      </header>
      <div className="px-4 pt-6 text-center">
        <div className="flex items-center justify-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[var(--trio-primary)] font-bold">
          <ScanLine className="w-4 h-4" /> Door Scanner
        </div>
        <p className="text-sm text-white/60 mt-1">Point at a ticket or RSVP pass QR. It checks them in automatically.</p>
      </div>
      <div className="px-4 pt-4 max-w-md w-full mx-auto">
        <DoorSummary refreshKey={count} dark />
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6">
        <div id="door-reader" data-testid="door-reader" className="w-full max-w-md aspect-square bg-white/5 overflow-hidden" />
        {working && <div className="mt-4 inline-flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</div>}
        {camError && (
          <div data-testid="door-cam-error" className="mt-4 text-center max-w-sm">
            <p className="text-sm text-red-300">{camError}</p>
            <button onClick={start} data-testid="door-start-camera" className="mt-3 inline-flex items-center gap-2 bg-[var(--trio-primary)] px-5 py-3 text-xs uppercase tracking-widest font-bold">
              <Camera className="w-4 h-4" /> Start Camera
            </button>
          </div>
        )}
      </div>
      <p className="text-center text-[11px] text-white/40 pb-6 px-6">Tip: add this page to your home screen for one-tap access at the door.</p>
      {result && <ResultOverlay result={result} onNext={resume} />}
    </div>
  );
}
